import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import * as XLSX from 'xlsx';
import useStore from '../store/useStore';
import { LinearProgress, Zoom, Fade } from '@mui/material';

export default function ImportStudentsDialog({
  open,
  onClose,
  onImportSuccess,
}) {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState({
    index: 0,
    total: 0,
    currentName: '',
  });
  const [processedList, setProcessedList] = useState([]);

  const { socket } = useStore();

  useEffect(() => {
    if (socket) {
      socket.on('import_progress', (data) => {
        setProgress(data);
        if (data.currentName) {
          setProcessedList((prev) => [
            { id: Date.now() + Math.random(), name: data.currentName },
            ...prev.slice(0, 4),
          ]);
        }
      });
    }
    return () => {
      if (socket) socket.off('import_progress');
    };
  }, [socket]);

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
    setError(null);
    setProgress({ index: 0, total: 0, currentName: '' });
    setProcessedList([]);
  };

  const downloadReport = () => {
    if (!results || !results.importedCredentials) return;

    const ws = XLSX.utils.json_to_sheet(
      results.importedCredentials.map((c) => ({
        'Parent Name': c.name,
        'Login (Email/Username)': c.username,
        'Generated Password': c.password,
        'Student Name': c.student,
      }))
    );
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Imported Credentials');
    XLSX.writeFile(wb, 'Import_Credentials_Report.xlsx');
  };

  const handleUpload = async () => {
    if (!file) return;
    setLoading(true);
    setError(null);
    setResults(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await fetch('/api/admin/import-xlsx-students', {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });

      const data = await res.json();
      if (res.ok) {
        setResults(data);
        if (onImportSuccess) onImportSuccess();
      } else {
        setError(data.message || 'Import failed');
      }
    } catch (err) {
      setError('Network error');
    } finally {
      setLoading(false);
    }
  };

  const resetAndClose = () => {
    setFile(null);
    setResults(null);
    setError(null);
    setProgress({ index: 0, total: 0, currentName: '' });
    onClose();
  };

  const percent =
    progress.total > 0
      ? Math.round((progress.index / progress.total) * 100)
      : 0;

  return (
    <Dialog open={open} onClose={resetAndClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>
        Bulk Student Import (XLSX)
      </DialogTitle>
      <DialogContent>
        {!results && !loading && (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography variant="body1" gutterBottom>
              Upload your XLSX file with standard CRM columns (ФИО, Заказчик,
              E-mail, etc.)
            </Typography>
            <input
              accept=".xlsx, .xls"
              style={{ display: 'none' }}
              id="import-file-upload"
              type="file"
              onChange={handleFileChange}
            />
            <label htmlFor="import-file-upload">
              <Button
                variant="outlined"
                component="span"
                startIcon={<CloudUploadIcon />}
                sx={{ mt: 2, mb: 2 }}
              >
                {file ? file.name : 'Select XLSX File'}
              </Button>
            </label>
            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </Box>
        )}

        {loading && (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <CircularProgress size={50} thickness={4} sx={{ mb: 3 }} />
            <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>
              Importing Students...
            </Typography>

            <Box sx={{ width: '100%', mt: 2, px: 4 }}>
              <LinearProgress
                variant="determinate"
                value={percent}
                sx={{ height: 12, borderRadius: 6, mb: 1 }}
              />
              <Box
                sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Processed: {progress.index} / {progress.total}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 800 }}>
                  {percent}%
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                mt: 2,
                minHeight: 180,
                bgcolor: 'grey.50',
                p: 2,
                borderRadius: 2,
                border: '1px dashed',
                borderColor: 'grey.300',
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: 'block',
                  mb: 2,
                  textTransform: 'uppercase',
                  letterSpacing: 1,
                  fontWeight: 700,
                }}
              >
                Recent Activity
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {processedList.length === 0 && (
                  <Typography
                    variant="body2"
                    color="text.disabled"
                    sx={{ fontStyle: 'italic', mt: 4 }}
                  >
                    Starting process...
                  </Typography>
                )}
                {processedList.map((item, idx) => (
                  <Fade in={true} key={item.id}>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: idx === 0 ? 'primary.50' : 'background.paper',
                        borderColor: idx === 0 ? 'primary.light' : 'grey.200',
                        transform: `scale(${1 - idx * 0.05})`,
                        opacity: 1 - idx * 0.2,
                        transition: 'all 0.3s ease',
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: idx === 0 ? 700 : 400,
                          color: idx === 0 ? 'primary.main' : 'text.primary',
                        }}
                      >
                        {idx === 0 ? '✓ ' : ''}
                        {item.name}
                      </Typography>
                    </Paper>
                  </Fade>
                ))}
              </Box>
            </Box>
          </Box>
        )}

        {results && (
          <Box sx={{ mt: 2 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 2,
                mb: 2,
              }}
            >
              <Alert severity="success" sx={{ flexGrow: 1 }}>
                Import complete! Created/Updated{' '}
                {results.results.created + results.results.updated} student
                records.
              </Alert>
              {results.results.errors && results.results.errors.length > 0 && (
                <Alert severity="warning" sx={{ flexGrow: 1 }}>
                  {results.results.errors.length} rows had errors.
                </Alert>
              )}
              {results.importedCredentials.length > 0 && (
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<DownloadIcon />}
                  onClick={downloadReport}
                >
                  Download XLSX Report
                </Button>
              )}
            </Box>

            {results.importedCredentials.length > 0 ? (
              <Zoom in={true}>
                <Box>
                  <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                    Generated Parent Credentials
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    sx={{ mb: 2 }}
                  >
                    Please save these credentials. Passwords are only shown
                    once.
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead sx={{ bgcolor: 'action.hover' }}>
                        <TableRow>
                          <TableCell>Parent Name</TableCell>
                          <TableCell>Username (Login)</TableCell>
                          <TableCell>Password</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {results.importedCredentials.map((cred, idx) => (
                          <TableRow key={idx}>
                            <TableCell>{cred.name}</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>
                              {cred.username}
                            </TableCell>
                            <TableCell sx={{ fontFamily: 'monospace' }}>
                              {cred.password}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </Zoom>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                No new credentials generated (all parents already existed).
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={resetAndClose} color="inherit">
          Close
        </Button>
        {!results && (
          <Button
            onClick={handleUpload}
            variant="contained"
            disabled={!file || loading}
            startIcon={loading && <CircularProgress size={20} />}
          >
            {loading ? 'Processing...' : 'Start Import'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
